import React from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams } from "react-router";
import { useForm, Controller } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import loader from "@/images/loader.gif";
import PlacePicker from "@/components/PlacePicker";
import { normalizeLocationMap } from "@/utils/location";
import { createHub } from "@/store/hubs/hubActions";
import {
  buttonRowSx,
  fieldSx,
  formSx,
  labelSx,
  pageIndexSx,
  questionSx,
  requiredPromptSx,
  requiredSx,
  titleSx,
} from "./styles";

interface OrganizationType {
  id: number;
  label: string;
}

interface StepOneValues {
  location_map: unknown;
  organization_type_id: string;
  website: string;
}

const CreateHubStepOne = () => {
  const { t } = useTranslation(["translation.Hub"]);
  const dispatch = useDispatch();
  useParams(); // kept for route stability; h_id no longer needed by createHub

  const { organizationTypes, hubName, isRequesting } = useSelector(
    (state: any) => {
      const { appData, metadata, invitations } = state;
      const processingHubInvitation = invitations.createHubInvitations.find(
        (chi: any) => chi.get("token") === invitations.createHubInvitationToken
      );

      return {
        organizationTypes: appData.organizationTypes
          .sortBy((ot: any) => ot.get("label"))
          .toJS() as OrganizationType[],
        hubName: processingHubInvitation.get("hub_name") as string,
        isRequesting: Boolean(metadata.isRequesting),
      };
    },
    shallowEqual
  );

  const { register, handleSubmit, control } = useForm<StepOneValues>({
    defaultValues: {
      location_map: null,
      organization_type_id: "",
      website: "",
    },
  });

  const onSubmit = (values: StepOneValues) => dispatch(createHub(values));

  return (
    <Dialog
      open
      // Esc goes back a step; a backdrop click does not, so a stray click
      // outside the form cannot discard a half-filled hub.
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") window.history.back();
      }}
      maxWidth={false}
      slotProps={{ paper: { "aria-label": t("CreateHubModal") } }}
    >
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={formSx}>
          <Typography variant="h2" sx={titleSx}>
            {t("CreateYourHub")} - {hubName}
          </Typography>
          <Box sx={requiredPromptSx}>{t("*required")}</Box>

          <Box sx={questionSx}>
            <Box component="span" sx={{ ...labelSx, ...requiredSx }}>
              {t("Location")}
            </Box>
            <Box sx={fieldSx}>
              <Controller
                name="location_map"
                control={control}
                render={({ field }) => (
                  <PlacePicker
                    value={normalizeLocationMap(field.value)}
                    onChange={field.onChange}
                    appearance="bare"
                    label={t("Location")}
                    placeholder={t("LocationPlaceholder")}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={questionSx}>
            <Box component="span" sx={{ ...labelSx, ...requiredSx }}>
              {t("Type")}
            </Box>
            <Box>
              <select {...register("organization_type_id")}>
                <option disabled hidden value="">
                  {t("SelectType")}
                </option>
                {organizationTypes.map((ot) => (
                  <option key={ot.id} value={ot.id}>
                    {ot.label}
                  </option>
                ))}
              </select>
            </Box>
          </Box>

          <Box sx={questionSx}>
            <Box component="span" sx={labelSx}>
              {t("Website")}
            </Box>
            <input
              type="text"
              placeholder="https://www.hub-name.com"
              {...register("website")}
            />
          </Box>

          <Stack direction="row" sx={buttonRowSx}>
            <Box component="span" sx={pageIndexSx}>
              1/2
            </Box>
            {isRequesting ? (
              <Button disabled startIcon={<img src={loader} alt="" />}>
                {t("Working")}
              </Button>
            ) : (
              <Button type="submit" variant="contained">
                {t("Next")} &gt;
              </Button>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateHubStepOne;
